/**
 * engineTodosDataSource.ts — TodosDataSource backed by /api/todos.
 *
 * Adapts the engine's TodoRow shape into the view's TodoListItem at the
 * boundary. Adapter rules:
 *
 *   - parent_card_id: engine `source_type ∈ {'deal','process','ticket'}`
 *     + `source_id` → exposed as a card linkage. For G2, the linkage
 *     surface is read-only display (Card column shows "Deal #N" / etc.);
 *     click-through to the card modal is disabled (supportsCardClickThrough
 *     is false) because engine doesn't inline card metadata on todo rows
 *     and the view needs the full UnifiedCardData to render the modal.
 *   - board: null for G2. The engine's todo doesn't carry a board name
 *     denormalized; mapping `source_type` → board would require a
 *     side-fetch per row. Deferred.
 *   - department: derived from `category` (engine uses category as a
 *     loose department-like tag) or falls back to 'standalone'.
 *   - assignee: only `assigned_to` numeric is reliable; the engine
 *     returns `assigned_name` joined from users.name but not initials/
 *     color. UI hydrates display from a users lookup or renders a
 *     fallback avatar.
 *   - skipped: not supported (no engine field). Always false. The view
 *     hides Skip controls when dataSource.supportsSkipped === false.
 *   - automation / template_id / template_override / instructions /
 *     custom_field_values: not surfaced by /api/todos. Adapter leaves
 *     them undefined; the table + modal render the empty state.
 *   - Dates parsed from SQLite-native via engineDateToIso.
 */
import { todosApi, type TodoRow, type CreateTodoInput, type UpdateTodoInput } from '../../../services/todosApi';
import { timeTrackingApi } from '../../../services/api';
import { engineDateToIso } from '../../../utils/engineDate';
import type {
  TodosDataSource,
  TodoPatch,
  CreateStandaloneTodoInput,
} from './todosDataSource';
import type { TodoListItem, TodoDepartment, EosLink } from '../standaloneTodos';
import type { TodoSummary, AssigneeDisplay } from '../../shared/UnifiedCard/types';
import type { AssigneeOption } from '../../shared/InlineEdit/types';
import { projectStageLabel } from '../../projects/projectsBoardConfig';

// ── Adapter ──────────────────────────────────────────────────────────────

const CARDLIKE_SOURCE_TYPES = new Set(['deal', 'process', 'ticket', 'process_instance', 'campaign_target']);

/** Where a to-do came from decides its department. */
const DEPARTMENT_BY_SOURCE: Record<string, TodoDepartment> = {
  // The sales pipeline.
  deal: 'sales',
  // The delivery spine.
  project: 'operations',
  // Marketing. `campaign_target` is already a known cardlike source below;
  // `marketing_content` is the Marketing Hub generator.
  campaign: 'marketing',
  campaign_contact: 'marketing',
  campaign_target: 'marketing',
  marketing_content: 'marketing',
};

function departmentForCategory(category: string | null | undefined, sourceType?: string | null): TodoDepartment {
  // Engine uses `category` loosely; map known buckets, fall back to standalone.
  const c = String(category ?? '').toLowerCase();
  if (c === 'sales') return 'sales';
  if (c === 'operations') return 'operations';
  if (c === 'marketing') return 'marketing';
  // P2-2: EOS bucket. Minting is unified on category='eos' going forward,
  // but pre-existing EOS todos were minted 'internal' (UI paths) — the
  // source_type fallback catches those without a data backfill.
  if (c === 'eos' || String(sourceType ?? '').startsWith('eos_')) return 'eos';

  // 2026-09-22 (Michael): "a todo from the pipeline should be auto tagged as
  // sales · tasks assigned to projects should be operations · tasks from
  // campaigns should be marketing."
  //
  // Same shape as the EOS fallback directly above, and for the same reason:
  // `category` is set inconsistently by the ~30 services that mint to-dos, so
  // reading it alone left the Department column lying. On production the two
  // biggest sources were each split three ways — deal to-dos were `internal`
  // (17) or `sales` (14); project to-dos were `client` (114), `operations`
  // (53) or `internal` (7). The department a to-do belongs to is a fact about
  // WHERE IT CAME FROM, not about which service happened to mint it, so it
  // derives from `source_type`.
  //
  // Deriving rather than backfilling fixes every existing row on the next
  // page load and cannot drift: a new minting site gets this for free, and
  // there is no second copy of the truth to keep in sync. An explicit
  // sales/operations/marketing `category` still wins above, so a deliberate
  // tag is never overridden.
  //
  // NOT touched: `todos.category`. It doubles as the `todo_statuses.section`
  // key, so rewriting it would swap a to-do's whole status vocabulary (a
  // project to-do would lose the `client` section's "Ready for Internal
  // Review" for the `internal` fallback). Department is a display + grouping
  // concern; category is a state-machine key. They stay separate.
  const bySource = DEPARTMENT_BY_SOURCE[String(sourceType ?? '').toLowerCase()];
  if (bySource) return bySource;

  return 'standalone';
}

/** Sprint X2: chip label + canonical deep-link for PROJECT-sourced todos
 *  (the sprint spine's seeded stage todos, source_type='project'). Projects
 *  aren't deal-cards — openCardModal resolves deals — so they ride their own
 *  link, mirroring the EOS pattern. The stage label is the project model's
 *  (server sends the raw `source_status`); a key the model can't name reads
 *  "Unsorted" — never the raw key. */
function projectLinkForRow(row: TodoRow): { label: string; href: string } | null {
  if (row.source_type !== 'project' || row.source_id == null || row.source_id === '') return null;
  const title =
    row.source_label && String(row.source_label).trim() !== ''
      ? String(row.source_label)
      : `#${row.source_id}`;
  const statusKey = row.source_status && String(row.source_status).trim() !== '' ? String(row.source_status) : null;
  const stageLabel = statusKey ? projectStageLabel(statusKey) : null;
  return {
    label: stageLabel ? `Project: ${title} · ${stageLabel}` : `Project: ${title}`,
    href: `/operations/projects/${row.source_id}`,
  };
}

/** P2-2: chip label + canonical deep-link for EOS-sourced todos. The rock
 *  routes need the OWNING rock id (eos_rock_id join) — a milestone todo's
 *  source_id is the milestone, which has no route of its own. */
function eosLinkForRow(row: TodoRow): EosLink | null {
  const label = row.source_label && String(row.source_label).trim() !== '' ? String(row.source_label) : null;
  switch (row.source_type) {
    // #9: carry `kind` + `id` so the /todos Card cell can branch on the
    // entity type — issues open IssueModal inline; rocks + L10 navigate.
    case 'eos_rock':
    case 'eos_rock_milestone':
      return row.eos_rock_id ? { label: `Rock: ${label ?? row.eos_rock_id}`, href: `/eos/rocks/${row.eos_rock_id}`, kind: 'rock', id: String(row.eos_rock_id) } : null;
    case 'eos_issue':
      return row.source_id ? { label: `Issue: ${label ?? row.source_id}`, href: `/eos/ids/${row.source_id}`, kind: 'issue', id: String(row.source_id) } : null;
    case 'eos_l10':
      return row.source_id ? { label: label ?? 'L10 meeting', href: `/eos/meetings/${row.source_id}`, kind: 'l10', id: String(row.source_id) } : null;
    default:
      return null;
  }
}

// Exported (bug-fix 2026-05-29) so UnifiedCardDetailModal can adapt a
// freshly-fetched single TodoRow when the user opens an engine todo
// detail modal without going through the data source's bulk fetchPage.
export function adaptTodoRow(row: TodoRow): TodoListItem {
  const isCardAttached =
    row.source_type != null &&
    CARDLIKE_SOURCE_TYPES.has(String(row.source_type)) &&
    row.source_id != null &&
    row.source_id !== '';

  // Bug-fix 2026-05-29: prefer the joined display name (source_label) when
  // the server brings one back. Fall back to the placeholder label so card
  // types without a JOIN (process / ticket) still render their identifier.
  const parentCardTitle = isCardAttached
    ? row.source_label && String(row.source_label).trim() !== ''
      ? String(row.source_label)
      : row.source_type === 'deal'
        ? `Deal #${row.source_id}`
        : row.source_type === 'process'
          ? `Process #${row.source_id}`
          : row.source_type === 'ticket'
            ? `Ticket #${row.source_id}`
            : `${row.source_type} ${row.source_id}`
    : null;

  // G2.1 A1: hydrate assignee display from the assignees cache when
  // available so the avatar gets the deterministic color we minted for
  // the picker. Falls back to a name-derived display when the cache is
  // empty (e.g. adapter runs before loadAssigneesOnce primes it).
  let assigneeDisplay: AssigneeDisplay | null = null;
  if (row.assigned_to != null) {
    const cached = assigneesCache?.find((a) => a.id === row.assigned_to);
    if (cached && cached.id != null) {
      assigneeDisplay = { name: cached.name, initials: cached.initials, color: cached.color };
    } else if (row.assigned_name) {
      assigneeDisplay = {
        name: row.assigned_name,
        initials: initialsFor(row.assigned_name),
        color: deterministicColor(String(row.assigned_to) + (row.assigned_name || '')),
      };
    }
  }

  return {
    id: row.id,
    parent_card_id: isCardAttached ? String(row.source_id) : null,
    parent_card_title: parentCardTitle,
    // Bug-fix 2026-05-29: server-side join brings the pipeline name back
    // as board_name for deal-attached todos. Pre-fix this was always null
    // → em-dash in the /todos Board column.
    board: row.board_name && String(row.board_name).trim() !== '' ? String(row.board_name) : null,
    department: departmentForCategory(row.category, row.source_type),
    eos_link: eosLinkForRow(row),
    project_link: projectLinkForRow(row),
    assignee_id: row.assigned_to ?? null,
    assignee: assigneeDisplay,
    assigned_by_id: row.assigned_by ?? null,
    assigned_by_name: row.assigned_by_name ?? null,
    title: row.title ?? '(untitled todo)',
    task_type: (row.task_type as TodoSummary['task_type']) ?? 'todo',
    due_date: row.due_date ?? null,
    completed: row.is_completed === true || row.is_completed === ('1' as unknown as boolean),
    // The engine DOES have a skipped field now — this was hardcoded `false`
    // under a G2.1 C1 comment saying it did not, and the comment went stale.
    // `todosApi.TodoRow` carries `state` / `skipped_at` / `skipped_by` (added
    // in c1.2, whose own comment notes "Both ends extended"), and the backend
    // writes `state='skipped'` from POST /:id/skip.
    //
    // Caught on production 2026-09-21 by the archive: the API reported 61 open
    // to-dos and this surface claimed 93. The 32-row gap was every skipped
    // to-do, counted as ACTIVE, while the Skipped tab read 0 — a to-do someone
    // had explicitly set aside kept nagging from the active count. Same defect
    // class as the header count this ticket set out to fix.
    //
    // READ only. `supportsSkipped` stays false on purpose: it gates the Skip
    // CONTROL, and nothing in the app can un-skip a to-do yet
    // (deferred-2026-09-154), so offering the action more widely would be
    // offering a one-way door.
    skipped: row.state === 'skipped',
    created_at: engineDateToIso(row.created_at) || row.created_at || new Date(0).toISOString(),
    // Phase 5.2: surface the client linkage so the modal can render the
    // Client meta-row + the dashboard widget can group/filter by client.
    // Engine /api/todos already joins `aptly_portfolio_clients.client_name`
    // onto the row (see TodoRow shape in todosApi).
    client_card_id: row.client_card_id ?? null,
    client_name: row.client_name ?? null,
    // feat/fathom-hub P5.2 round-trip fix: the modal's Instructions field
    // SAVES into the engine `description` column (see patchToUpdateInput /
    // createInputToApi below) but the read side never mapped it back, so
    // every description — including the Fathom "Watch the moment" call
    // links the hub triage writes — rendered as an empty Instructions box.
    instructions: row.description && String(row.description).trim() !== '' ? String(row.description) : undefined,
    engine: row,
    // automation / template_* / custom_field_values left undefined —
    // engine doesn't surface them. Renders empty-state cleanly.
  };
}

// ── Patch translator ─────────────────────────────────────────────────────

function patchToUpdateInput(patch: TodoPatch): UpdateTodoInput {
  const out: UpdateTodoInput = {};
  if ('title' in patch && patch.title !== undefined) out.title = patch.title;
  if ('due_date' in patch) out.due_date = patch.due_date ?? null;
  if ('assignee_id' in patch) out.assigned_to = patch.assignee_id ?? null;
  if ('task_type' in patch && patch.task_type !== undefined) out.task_type = String(patch.task_type);
  if ('instructions' in patch && patch.instructions !== undefined) out.description = patch.instructions;
  // G2.1 A2: department maps to engine's `category` field (the engine's
  // loose department-like tag — see adapter notes above). Empty string
  // when department is 'standalone' so engine doesn't filter against an
  // FE-only literal.
  if ('department' in patch && patch.department !== undefined) {
    out.category = patch.department === 'standalone' ? 'internal' : patch.department;
  }
  // Phase 5.2: client linkage flush. `null` explicitly detaches.
  if ('client_card_id' in patch) {
    out.client_card_id = patch.client_card_id ?? null;
  }
  // NB: `completed` is intentionally NOT passed through here. The engine's
  // todoService.updateTodo allowed-fields list excludes is_completed —
  // toggling completion happens via POST /:id/complete or /:id/reopen,
  // dispatched in updateItem below. See G2.1 A3.
  //
  // G2.1 C1 (Skip deferral): `skipped` has no engine field. Engine chat
  // needs to add a `skipped` column to /api/todos schema before the FE
  // can re-show Skip controls (supportsSkipped is false today).
  //
  // G2.1 A4 (Automation deferred): `automation`, `template_*`,
  // `custom_field_values` likewise unsupported by /api/todos schema.
  // ManualToAutomatedControl is hidden in engine mode (supportsAutomation).
  return out;
}

// ── Factory ──────────────────────────────────────────────────────────────

// ── Assignees cache ──────────────────────────────────────────────────────
// G2.1 A1: engine assignee picker reads from /api/time-tracking/users (the
// same endpoint legacy TodosView uses). Cached at module scope so the
// picker doesn't re-fetch per open. The cache lives until page refresh —
// roster changes mid-session require a reload, which matches legacy
// behavior.

let assigneesCache: AssigneeOption[] | null = null;
let assigneesPromise: Promise<AssigneeOption[]> | null = null;

function deterministicColor(seed: string): string {
  // Pick a paprika-adjacent hue from a small palette so multiple users
  // get distinguishable avatars without requiring the engine to emit a
  // color column. Engine response shape today is { id, display_name,
  // email } only.
  const palette = ['#DC2626', '#7C3AED', '#059669', '#0284C7', '#D97706', '#DB2777', '#0891B2'];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
}

function initialsFor(name: string): string {
  if (!name) return '?';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + (parts[parts.length - 1][0] ?? '')).toUpperCase();
}

async function loadAssigneesOnce(): Promise<AssigneeOption[]> {
  if (assigneesCache) return assigneesCache;
  if (assigneesPromise) return assigneesPromise;
  assigneesPromise = (async () => {
    try {
      const res = await timeTrackingApi.getUsers();
      const users = res.success ? res.data : [];
      const opts: AssigneeOption[] = users.map((u) => ({
        id: u.id,
        name: u.display_name || u.email || `User #${u.id}`,
        initials: initialsFor(u.display_name || u.email || ''),
        color: deterministicColor(String(u.id) + (u.display_name || '')),
      }));
      // Always include an Unassigned option at the tail so users can clear
      // an assignment from the picker.
      opts.push({ id: null, name: 'Unassigned', initials: '—', color: '#94a3b8' });
      assigneesCache = opts;
      return opts;
    } catch {
      const fallback: AssigneeOption[] = [
        { id: null, name: 'Unassigned', initials: '—', color: '#94a3b8' },
      ];
      assigneesCache = fallback;
      return fallback;
    } finally {
      assigneesPromise = null;
    }
  })();
  return assigneesPromise;
}

// ── Data source ──────────────────────────────────────────────────────────

export const engineTodosDataSource: TodosDataSource = {
  mode: 'engine',
  supportsCardClickThrough: false,
  // The engine added the skipped field long ago (POST /:id/skip, `state`,
  // `skipped_at`, `skipped_by`); this flag was never turned back on, so
  // `handleToggleSkip` bailed on the first line and Skip — row AND bulk — did
  // nothing at all on /operations/todos. No request, no toast, no error: the
  // selection just cleared as though it had worked. The cruel part is that
  // the server's own refusal message for an email to-do is "must be sent or
  // skipped", pointing at a control that was inert.
  //
  // Safe to enable now that `POST /:id/reactivate` is mounted — before it,
  // turning Skip on would have handed users a one-way door
  // (deferred-2026-09-154, closed in the same change).
  supportsSkipped: true,
  supportsAutomation: false, // G2.1 A4: hidden until engine adds automation fields.

  loadItems: async () => {
    // G2.1 A1: prime the assignees cache in parallel so adaptTodoRow can
    // hydrate display fields with the colors minted for the picker. The
    // two requests fire concurrently; loadAssigneesOnce dedupes if the
    // picker beat us to it.
    //
    // Load the WHOLE set, not a page of it.
    //
    // History: perPage went 200 → 500 (2026-05-29 vanishing-todo polish)
    // because EngineTodosView filters, searches, sorts and COUNTS purely
    // client-side, so any row outside the fetched `created_at DESC` page
    // was invisible to every chip on the surface. 500 was still a ceiling,
    // and on 2026-09-21 production had 1,728 todos: the surface showed the
    // newest 500 (nothing created before 2026-06-22), reported "358 active"
    // when 476 were open, and sorted inside a window rather than across the
    // list. Every number on the page described a slice the user could not
    // see or reason about.
    //
    // Paging to exhaustion is the honest fix while the chain stays
    // client-side. The ceiling below is a runaway guard, not a page size —
    // at 1k/request today's set costs two round trips. Server-side filter +
    // count pushdown remains the real answer (deferred-2026-09-181); it
    // belongs with the todos upgrade, not with this count fix.
    const PAGE_SIZE = 1000;
    const MAX_PAGES = 10;
    const [firstRes] = await Promise.all([
      todosApi.list({ perPage: PAGE_SIZE, page: 1 }),
      loadAssigneesOnce(),
    ]);
    const rows: TodoRow[] = [...firstRes.data];
    // A full page means there may be more. A short page means we're done.
    let page = 1;
    while (rows.length === page * PAGE_SIZE && page < MAX_PAGES) {
      page += 1;
      const next = await todosApi.list({ perPage: PAGE_SIZE, page });
      rows.push(...next.data);
    }
    // De-dupe by id. The pages are LIMIT/OFFSET windows over a live
    // `created_at DESC` list, so a to-do written BETWEEN two requests shifts
    // every later row down one and page N+1 re-serves the last row of page N.
    // That is not hypothetical here — automations create to-dos on their own
    // schedule, and the table keys rows by id, so a duplicate means a repeated
    // React key and a count that is off by one until the next reload. Keeping
    // the FIRST copy preserves the server's ordering.
    const seen = new Set<TodoRow['id']>();
    const unique = rows.filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)));
    return unique.map(adaptTodoRow);
  },

  // Engine mode doesn't surface card-attached metadata inline; the view's
  // click-through path is disabled so this returns empty.
  loadCards: async () => [],

  getAssignees: async (currentDisplay?: AssigneeDisplay | null) => {
    const opts = await loadAssigneesOnce();
    // Stash the current item's assignee display at the front if it's not
    // in the active user list — keeps stale/legacy assignees visible.
    if (currentDisplay && !opts.some((o) => o.name === currentDisplay.name)) {
      return [
        {
          id: null,
          name: currentDisplay.name,
          initials: currentDisplay.initials,
          color: currentDisplay.color,
        },
        ...opts,
      ];
    }
    return opts;
  },

  updateItem: async (item, patch) => {
    const numId = typeof item.id === 'number' ? item.id : Number(item.id);
    if (!Number.isFinite(numId)) return;

    // G2.1 A3: completion routes through the dedicated endpoint. The PATCH
    // route's allowed-fields list excludes is_completed (it's derived
    // from status_id at the server), so toggling completion via update
    // is a silent no-op. Use complete/reopen instead.
    //
    // 2026-09-22 — these used to be `catch { /* surface via toast at the
    // caller level */ }`. Swallowing made that comment impossible to honour:
    // the promise RESOLVED, so the caller's `.catch(revert)` never ran, the
    // optimistic row stayed ticked, no toast appeared, and the next refresh
    // silently put the to-do back. Michael hit it head-on — bulk "Mark
    // complete" on a list where 43 of 69 open to-dos were email/scheduling
    // types, every one of which the server REFUSES on purpose with
    // `400 action_type_requires_execution` ("Email tasks must be sent or
    // skipped, not checked off."). The app had the right answer and ate it.
    //
    // Errors now propagate. The view reverts AND shows the server's own
    // message, so a refusal reads as a refusal instead of as a save that
    // quietly didn't happen.
    if ('completed' in patch && patch.completed !== undefined) {
      if (patch.completed) await todosApi.complete(numId);
      else await todosApi.reopen(numId);
    }

    // Skip is a state transition with its own endpoints, not a column write —
    // `patchToUpdateInput` has never carried it, which is the other half of
    // why Skip did nothing on this surface.
    if ('skipped' in patch && patch.skipped !== undefined) {
      if (patch.skipped) await todosApi.skip(numId);
      else await todosApi.reactivate(numId);
    }

    const input = patchToUpdateInput(patch);
    if (Object.keys(input).length === 0) return;
    await todosApi.update(numId, input);
  },

  removeItem: async (item) => {
    const numId = typeof item.id === 'number' ? item.id : Number(item.id);
    if (!Number.isFinite(numId)) return;
    try {
      await todosApi.remove(numId);
    } catch {
      /* see updateItem */
    }
  },

  createStandalone: async (input: CreateStandaloneTodoInput) => {
    const created = await todosApi.create({
      title: input.title,
      task_type: input.task_type ?? 'todo',
      assigned_to: input.assignee_id ?? null,
      due_date: input.due_date ?? null,
      category: input.department === 'standalone' ? undefined : input.department,
      description: input.instructions,
      // Phase 5.2: client_card_id flows through create when the modal's
      // Client picker is filled before submit.
      client_card_id: input.client_card_id ?? null,
    });
    return adaptTodoRow(created.data);
  },
};
